import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { ALL_PAYMENT_RECORD_STATUSES, type PaymentView } from '@lims/shared-types';
import { clientsApi, paymentsApi } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionButton,
  DataTable,
  FilterBar,
  PageHeader,
  Panel,
  SearchField,
  SelectField,
  StatusBadge,
  type DataColumn,
} from '../../components/ui/index.js';
import { formatDate, formatMoney } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { RecordLink } from '../clients/record-layout.js';

/** The payment register: money received, and what it has settled. */
export function PaymentsPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const locale = currentLocale();

  const [search, setSearch] = useState('');
  const [clientId, setClientId] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const clients = useQuery({
    queryKey: ['clients', 'selectable'],
    queryFn: () => clientsApi.list({ pageSize: 100, includeSuspended: false }),
  });

  const payments = useQuery({
    queryKey: ['payments', { page, search, clientId, status }],
    queryFn: () =>
      paymentsApi.list({
        page,
        search,
        ...(clientId ? { clientId } : {}),
        ...(status ? { status } : {}),
      }),
  });

  const columns: readonly DataColumn<PaymentView>[] = [
    {
      key: 'client',
      header: t('payments.client'),
      render: (payment) => (
        // The client opens the payment: the row's way in (D278). A payment
        // carries no number of its own; its receipt reference sits beneath.
        <div className="min-w-0">
          <span className="block font-semibold">
            <RecordLink to={`/payments/${payment.id}`}>
              {payment.client.name ?? payment.client.number}
            </RecordLink>
          </span>
          {payment.reference ? (
            <span className="measure text-caption text-default-600" dir="ltr">
              {payment.reference}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'amount',
      header: t('payments.amount'),
      align: 'end',
      render: (payment) => (
        <span className="measure text-default-900" dir="ltr">
          {formatMoney(payment.amountMinor, { locale, currency: payment.currency })}
        </span>
      ),
    },
    {
      key: 'unallocated',
      header: t('payments.unallocated'),
      align: 'end',
      render: (payment) => (
        <span className="measure text-default-800" dir="ltr">
          {formatMoney(payment.unallocatedMinor, {
            locale,
            currency: payment.currency,
          })}
        </span>
      ),
    },
    {
      key: 'receivedOn',
      header: t('payments.receivedOn'),
      render: (payment) => (
        <span className="whitespace-nowrap">{formatDate(payment.receivedOn, { locale })}</span>
      ),
    },
    {
      key: 'method',
      header: t('payments.methodLabel'),
      render: (payment) => t(`paymentMethod.${payment.method}`),
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (payment) => (
        <StatusBadge
          label={t(`status.paymentRecord.${payment.status}`)}
          tone={payment.status === 'reversed' ? 'warning' : 'success'}
        />
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      isHeaderHidden: true,
      align: 'end',
      render: (payment) => (
        <ActionButton
          priority="tertiary"
          size="sm"
          onPress={() => navigate(`/payments/${payment.id}`)}
        >
          {t('common.open')}
        </ActionButton>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('payments.title')}
        subtitle={t('payments.subtitle')}
        breadcrumbs={[{ label: t('nav.payments') }]}
        actions={
          can(PERMISSIONS.PAYMENTS_RECORD) ? (
            <ActionButton
              priority="primary"
              onPress={() => navigate('/payments/new')}
            >
              {t('payments.record')}
            </ActionButton>
          ) : null
        }
      />

      <Panel>
        <FilterBar>
          <SearchField
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder={t('payments.searchPlaceholder')}
          />
          <SelectField
            label={t('payments.client')}
            value={clientId}
            onChange={(value) => {
              setClientId(value);
              setPage(1);
            }}
            placeholder={t('common.all')}
            options={(clients.data?.data ?? []).map((client) => ({
              value: client.id,
              label: client.name ?? client.clientNumber,
            }))}
          />
          <SelectField
            label={t('common.status')}
            value={status}
            onChange={(value) => {
              setStatus(value);
              setPage(1);
            }}
            placeholder={t('common.all')}
            options={ALL_PAYMENT_RECORD_STATUSES.map((value) => ({
              value,
              label: t(`status.paymentRecord.${value}`),
            }))}
          />
        </FilterBar>

        <DataTable
          label={t('payments.title')}
          columns={columns}
          rows={payments.data?.data ?? []}
          rowKey={(payment) => payment.id}
          isLoading={payments.isLoading}
          error={payments.error}
          onRetry={() => void payments.refetch()}
          emptyContent={search ? t('payments.noneFound') : t('payments.noneYet')}
          page={page}
          totalPages={payments.data?.meta.totalPages ?? 1}
          onPageChange={setPage}
        />
      </Panel>
    </>
  );
}
